import { createWizardFromDeclaration } from "@/components/component-wizard/declarative-converter"
import type { DeclarativeWizardQuestion } from "@/components/component-wizard/declarative-wizard"
import type { Wizard } from "@/components/component-wizard/wizard"
import { renderWizard } from "@/components/component-wizard/wizard-renderer"
import LoadingButton from "@/components/LoadingButton.vue"
import type { ChampionshipCategory } from "@/model/championship/championship-category"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import {
    type ChampionshipRegistrationTeamMember,
    type ChampionshipRegistrationTeamValidationResult,
    useChampionshipRegistrationSelectTeamData,
} from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import ChampionshipRegistrationEditTeam from "@/pages/Tasks/Tasks/internal/ChampionshipRegistrationEditTeam.vue"
import { defineTranslations } from "@/utils/defineTranslations"
import { injectMandatory, useHttpClient, useInlineTable } from "@/vf"
import { computed, defineComponent, h, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"

/*─────────────────────────────────────┐
│  Types                               │
└─────────────────────────────────────*/
export type CategoryWizardMode = "direct" | "changeRequest"

/*─────────────────────────────────────┐
│  Translations                        │
└─────────────────────────────────────*/
const trans = defineTranslations({
    choose_mode: {
        de: "Wie möchtest du die Kategorie ändern?",
        en: "How do you want to change the category?",
    },
    keep_members: {
        de: "Nur die Kategorie wechseln",
        en: "Only change the category",
    },
    keep_members_description: {
        de: "Die Mitglieder bleiben unverändert",
        en: "Members will remain unchanged",
    },
    change_members: {
        de: "Kategorie und Teilnehmer ändern",
        en: "Change category and members",
    },
    change_members_description: {
        de: "Du kannst auch die Teammitglieder anpassen",
        en: "You can also modify the team members",
    },
    no_compatible_categories: {
        de: 'Es gibt keine kompatiblen Kategorien für die aktuellen Teammitglieder. Bitte nutze die Option "Kategorie und Teilnehmer ändern".',
        en: 'There are no compatible categories for the current team members. Please use the "Change category and members" option.',
    },
    please_provide_category: {
        de: "Bitte wähle eine Kategorie",
        en: "Please choose a category",
    },
    category: {
        de: "Kategorie",
        en: "Category",
    },
    please_select_category: {
        de: "Bitte Kategorie auswählen",
        en: "Please select a category",
    },
    validating: {
        de: "Überprüfe Kategorie...",
        en: "Validating category...",
    },
    validation_success: {
        de: "Wechsel zu dieser Kategorie ist mit den aktuellen Teammitgliedern möglich.",
        en: "Changing to this category is possible with the current team members.",
    },
    continue: {
        de: "Weiter",
        en: "Continue",
    },
    submit: {
        de: "Kategorie wechseln",
        en: "Change category",
    },
    submit_change_request: {
        de: "Änderung beantragen",
        en: "Request change",
    },
    change_request_submitted: {
        de: "Deine Änderungsanfrage wurde eingereicht und wird vom Office geprüft.",
        en: "Your change request has been submitted and will be reviewed by the office.",
    },
    edit_members: {
        de: "Teilnehmer bearbeiten",
        en: "Edit members",
    },
    validation_warning_with_blockers: {
        de: "Du kannst die Kategorie wechseln. Bitte überprüfe anschließend deine Meldung.",
        en: "You can change the category. Please check your registration.",
    },
})

/*─────────────────────────────────────┐
│  KeepMembersFlow component           │
│  Category dropdown + validation      │
└─────────────────────────────────────*/
const KeepMembersFlow = defineComponent<
    {
        registrationId: string
        team: ChampionshipRegistrationTeam
        wizard: Wizard<null>
        mode: CategoryWizardMode
    },
    { saved: () => void }
>({
    props: ["registrationId", "team", "wizard", "mode"],
    emits: ["saved"],
    setup(props, { emit }) {
        const { t } = useI18n()
        const http = useHttpClient()
        const inlineTable = useInlineTable(false)
        const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
        const data = useChampionshipRegistrationSelectTeamData()
        const changeRequestSubmitted = ref(false)

        const selectedCategoryId = ref<string>("")
        const validationResult = ref<ChampionshipRegistrationTeamValidationResult>(null)
        const validating = ref(false)
        const submitting = ref(false)

        // Current members (kept unchanged)
        const members = ref<ChampionshipRegistrationTeamMember[]>(
            props.team.members!.map(member => ({
                memberId: member.memberId!,
                type: member.type! as ChampionshipRegistrationTeamMember["type"],
            })),
        )

        // Categories compatible with the current team members
        const compatibleCategories = computed<ChampionshipCategory[]>(() => {
            if (!data.value?.categories) {
                return []
            }

            const team = props.team
            const currentCategoryId = team.championshipCategory?.id

            return data.value.categories.filter(category => {
                // Exclude current category
                if (category.id === currentCategoryId) {
                    return false
                }

                // Check max criteria (team must not exceed category limits)
                if (
                    category.criteriaMaxStarters !== undefined &&
                    (team.numberOfParticipants ?? 0) > category.criteriaMaxStarters
                ) {
                    return false
                }
                if (
                    category.criteriaMaxReplacements !== undefined &&
                    (team.numberOfAlternates ?? 0) > category.criteriaMaxReplacements
                ) {
                    return false
                }
                if (
                    category.criteriaMaxEntourages !== undefined &&
                    (team.numberOfEntourages ?? 0) > category.criteriaMaxEntourages
                ) {
                    return false
                }

                // Check min criteria (team must meet minimum requirements)
                if (
                    category.criteriaMinStarters !== undefined &&
                    (team.numberOfParticipants ?? 0) < category.criteriaMinStarters
                ) {
                    return false
                }

                return true
            })
        })

        const hasBlockers = computed(() => (validationResult.value?.blockers?.length ?? 0) > 0)
        const validationSuccessful = computed(
            () => validationResult.value !== null && !hasBlockers.value && !validating.value,
        )
        const canSubmit = computed(
            () =>
                selectedCategoryId.value &&
                (props.mode === "direct" || !hasBlockers.value) &&
                !submitting.value &&
                !validating.value,
        )

        function getMemberName(memberId: string | undefined): string | null {
            if (!memberId) {
                return null
            }

            const member = data.value?.availableMembers.find(m => m.id === memberId)
            if (member) {
                return `${member.firstName} ${member.lastName}`
            }

            return null
        }

        async function validateCategory() {
            if (!selectedCategoryId.value) {
                validationResult.value = null
                return
            }

            validating.value = true
            try {
                const result = await http.post<ChampionshipRegistrationTeamValidationResult>(
                    `/championship/registration/${data.value?.registrationId}/select-team/validate`,
                    {
                        members: members.value,
                        categoryId: selectedCategoryId.value,
                        teamId: props.team.id,
                    },
                )
                validationResult.value = result.data

                // Update breadcrumb with selected category name
                const category = compatibleCategories.value.find(c => c.id === selectedCategoryId.value)
                if (category) {
                    props.wizard.setCurrentAnswerLabel(category.name ?? null)
                }
            } finally {
                validating.value = false
            }
        }

        watch(selectedCategoryId, validateCategory)

        async function saveCategoryOnly() {
            if (!canSubmit.value) {
                return
            }

            submitting.value = true
            try {
                if (props.mode === "changeRequest") {
                    // Submit a change request instead of directly updating
                    await http.post(
                        `/championship/registration/${props.registrationId}/change-request/${props.team.id}/category?viewMode=${viewMode}`,
                        {
                            categoryId: selectedCategoryId.value,
                        },
                    )
                    changeRequestSubmitted.value = true
                    emit("saved")
                } else {
                    // Direct update
                    await http.put(
                        `/championship/registration/${props.registrationId}/team/${props.team.id}/category?viewMode=${viewMode}`,
                        {
                            categoryId: selectedCategoryId.value,
                        },
                    )
                    emit("saved")
                    inlineTable.close({ saved: true })
                }
            } finally {
                submitting.value = false
            }
        }

        return () => (
            <div class="px-2">
                {/* Show success message if change request was submitted */}
                {changeRequestSubmitted.value && (
                    <div class="alert alert-success">
                        <i class="fas fa-check-circle mr-2"></i>
                        {trans("change_request_submitted")}
                    </div>
                )}

                {!changeRequestSubmitted.value && (
                    <>
                        <div class="mb-3">
                            <b>{trans("please_provide_category")}</b>
                        </div>

                        {/* No compatible categories warning */}
                        {compatibleCategories.value.length === 0 && (
                            <div class="alert alert-warning">{trans("no_compatible_categories")}</div>
                        )}

                        {/* Category selection */}
                        {compatibleCategories.value.length > 0 && (
                            <form
                                onSubmit={e => {
                                    e.preventDefault()
                                    saveCategoryOnly()
                                }}
                            >
                                <label for="categoryIdKeepMembers" class="mt-3">
                                    {trans("category")}
                                </label>
                                <select
                                    id="categoryIdKeepMembers"
                                    class="form-control"
                                    value={selectedCategoryId.value}
                                    onChange={e => (selectedCategoryId.value = (e.target as HTMLSelectElement).value)}
                                >
                                    <option value="" disabled>
                                        {trans("please_select_category")}
                                    </option>
                                    {compatibleCategories.value.map(category => (
                                        <option value={category.id} key={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>

                                {/* Validation loading indicator */}
                                {validating.value && (
                                    <div class="mt-3 text-muted">
                                        <i class="fas fa-spinner fa-spin mr-2"></i> {trans("validating")}
                                    </div>
                                )}

                                {/* Show blockers - as error in changeRequest mode, as warning in direct mode */}
                                {hasBlockers.value && !validating.value && (
                                    <div
                                        class={`alert ${props.mode === "direct" ? "alert-warning" : "alert-danger"} mt-3`}
                                    >
                                        {props.mode === "direct" && (
                                            <div>{trans("validation_warning_with_blockers")}</div>
                                        )}
                                        {props.mode !== "direct" &&
                                            validationResult.value?.blockers?.map(blocker => (
                                                <div key={blocker.type + (blocker.memberId ?? "")}>
                                                    {getMemberName(blocker.memberId) && (
                                                        <strong>{getMemberName(blocker.memberId)}: </strong>
                                                    )}
                                                    {t("@championship:registration.blocker." + blocker.type)}
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {/* Show success message if validation passed */}
                                {validationSuccessful.value && (
                                    <div class="alert alert-success mt-3">
                                        <i class="fas fa-check-circle mr-2"></i>
                                        {trans("validation_success")}
                                    </div>
                                )}

                                <LoadingButton
                                    class="btn btn-primary mt-4"
                                    type="submit"
                                    disabled={!canSubmit.value}
                                    loading={submitting.value}
                                >
                                    {props.mode === "changeRequest" ? trans("submit_change_request") : trans("submit")}
                                </LoadingButton>
                            </form>
                        )}
                    </>
                )}
            </div>
        )
    },
})

/*─────────────────────────────────────┐
│  ChangeMembersFlowMemberEditor       │
│  Wizard step for editing members     │
└─────────────────────────────────────*/
const ChangeMembersFlowMemberEditor = defineComponent<
    {
        registrationId: string
        team: ChampionshipRegistrationTeam
        categoryId: string
        mode: CategoryWizardMode
    },
    { saved: () => void }
>({
    props: ["registrationId", "team", "categoryId", "mode"],
    emits: ["saved"],
    setup(props, { emit }) {
        const http = useHttpClient()
        const inlineTable = useInlineTable(false)
        const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")

        const submitting = ref(false)
        const members = ref<ChampionshipRegistrationTeamMember[]>(
            props.team.members!.map(member => ({
                memberId: member.memberId!,
                type: member.type! as ChampionshipRegistrationTeamMember["type"],
            })),
        )
        const changeRequestSubmitted = ref(false)

        async function saveWithMembers() {
            submitting.value = true
            try {
                if (props.mode === "changeRequest") {
                    // Submit a change request instead of directly updating
                    await http.post(
                        `/championship/registration/${props.registrationId}/change-request/${props.team.id}/category-with-members?viewMode=${viewMode}`,
                        {
                            members: members.value,
                            categoryId: props.categoryId,
                            teamName: props.team.teamName,
                        },
                    )
                    changeRequestSubmitted.value = true
                    emit("saved")
                } else {
                    // Direct update
                    await http.put(
                        `/championship/registration/${props.registrationId}/team/${props.team.id}/category-with-members?viewMode=${viewMode}`,
                        {
                            members: members.value,
                            categoryId: props.categoryId,
                            teamName: props.team.teamName,
                        },
                    )
                    emit("saved")
                    inlineTable.close({ saved: true })
                }
            } finally {
                submitting.value = false
            }
        }

        return () => (
            <div class="px-2">
                {/* Show success message if change request was submitted */}
                {changeRequestSubmitted.value && (
                    <div class="alert alert-success">
                        <i class="fas fa-check-circle mr-2"></i>
                        {trans("change_request_submitted")}
                    </div>
                )}

                {!changeRequestSubmitted.value && (
                    <ChampionshipRegistrationEditTeam
                        categoryId={props.categoryId}
                        team={props.team}
                        submitting={submitting.value}
                        modelValue={members.value}
                        onUpdate:modelValue={(val: ChampionshipRegistrationTeamMember[]) => (members.value = val)}
                        onSave={saveWithMembers}
                        v-slots={{
                            submitLabel:
                                props.mode === "changeRequest" ? () => trans("submit_change_request") : undefined,
                        }}
                    />
                )}
            </div>
        )
    },
})

/*─────────────────────────────────────┐
│  ChangeMembersFlow component         │
│  Category dropdown → Member editor   │
└─────────────────────────────────────*/
const ChangeMembersFlow = defineComponent<
    {
        registrationId: string
        team: ChampionshipRegistrationTeam
        wizard: Wizard<null>
        mode: CategoryWizardMode
    },
    { saved: () => void }
>({
    props: ["registrationId", "team", "wizard", "mode"],
    emits: ["saved"],
    setup(props, { emit }) {
        const data = useChampionshipRegistrationSelectTeamData()
        const selectedCategoryId = ref<string>("")

        // All categories except the current one
        const allCategories = computed<ChampionshipCategory[]>(() => {
            if (!data.value?.categories) return []

            const currentCategoryId = props.team.championshipCategory?.id
            return data.value.categories.filter(category => category.id !== currentCategoryId)
        })

        function proceedToMemberEdit() {
            if (!selectedCategoryId.value) return

            // Update breadcrumb with selected category name
            const category = allCategories.value.find(c => c.id === selectedCategoryId.value)
            if (category) {
                props.wizard.setCurrentAnswerLabel(category.name ?? null)
            }

            // Create and append a new wizard step for the member editor
            const memberEditorComponent = props.wizard.component(trans("edit_members"), () =>
                h(ChangeMembersFlowMemberEditor, {
                    registrationId: props.registrationId,
                    team: props.team,
                    categoryId: selectedCategoryId.value,
                    mode: props.mode,
                    onSaved: () => emit("saved"),
                }),
            )

            props.wizard.append(memberEditorComponent)
        }

        return () => (
            <div class="px-2">
                <form
                    onSubmit={e => {
                        e.preventDefault()
                        proceedToMemberEdit()
                    }}
                >
                    <div class="mb-3">
                        <b>{trans("please_provide_category")}</b>
                    </div>

                    <label for="categoryIdChangeMembers" class="mt-3">
                        {trans("category")}
                    </label>
                    <select
                        id="categoryIdChangeMembers"
                        class="form-control"
                        value={selectedCategoryId.value}
                        onChange={e => (selectedCategoryId.value = (e.target as HTMLSelectElement).value)}
                    >
                        <option value="" disabled>
                            {trans("please_select_category")}
                        </option>
                        {allCategories.value.map(category => (
                            <option value={category.id} key={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>

                    <button class="btn btn-primary mt-4" type="submit" disabled={!selectedCategoryId.value}>
                        {trans("continue")}
                    </button>
                </form>
            </div>
        )
    },
})

/*─────────────────────────────────────┐
│  Main component                      │
└─────────────────────────────────────*/
export default defineComponent<
    {
        registrationId: string
        team: ChampionshipRegistrationTeam
        mode?: CategoryWizardMode
    },
    { save: () => void }
>({
    props: ["registrationId", "team", "mode"],
    emits: ["save"],
    setup(props, { emit }) {
        // Default to 'direct' mode for backwards compatibility
        const mode = props.mode ?? "direct"
        const data = useChampionshipRegistrationSelectTeamData()
        const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
        const policy = useRegistrationPolicy(computed(() => data.value?.permissions), computed(() => viewMode))

        const keepMembersAllowed =
            mode === "changeRequest"
                ? policy.value.ChangeRequestCreateCategory
                : policy.value.ChangeTeamCategory

        const changeMembersAllowed =
            mode === "changeRequest"
                ? policy.value.ChangeRequestCreateCategoryWithMembers
                : policy.value.ChangeTeamCategoryWithMembers

        /*─────────────────────────────────────┐
        │  Wizard declaration                  │
        └─────────────────────────────────────*/
        const wizardDeclaration: DeclarativeWizardQuestion<null> = {
            question: trans("choose_mode"),
            options: [
                {
                    answer: trans("keep_members"),
                    disabled: !keepMembersAllowed,
                    action: ({ wizard }) =>
                        h(KeepMembersFlow, {
                            registrationId: props.registrationId,
                            team: props.team,
                            wizard,
                            mode,
                            onSaved: () => emit("save"),
                        }),
                },
                {
                    answer: trans("change_members"),
                    disabled: !changeMembersAllowed,
                    action: ({ wizard }) =>
                        h(ChangeMembersFlow, {
                            registrationId: props.registrationId,
                            team: props.team,
                            wizard,
                            mode,
                            onSaved: () => emit("save"),
                        }),
                },
            ],
        }

        const wizard = createWizardFromDeclaration(wizardDeclaration, null)

        return () => renderWizard({ wizard })
    },
})
